import { Card } from '@braze/web-sdk';
import { track } from '@eventbrite/datalayer-library';
import React from 'react';
import ReactDOM from 'react-dom';
import { getBrazeInstance } from '../client';
import {
    DEFAULT_NOTIFICATION_TEMPLATE_ID,
    NOTIFICATION_TEMPLATES,
} from '../constants/notificationTemplates';
import { renderCustomNoCardsMessage } from './CustomBrazeNoCardsRenderer';
import { customizeDrawerHeader } from './DrawerHeader';
import './NotificationUIManager.css';

export function mountBrazeContentCardsUI(cards: Card[]): void {
    setTimeout(() => {
        customizeDrawerHeader();
        attachDrawerScrollListener();
        attachDrawerDismissHandler();

        if (!cards || cards.length === 0) {
            renderCustomNoCardsMessage();
            return;
        }

        const cardElements = document.querySelectorAll('.ab-card');
        cardElements.forEach((cardEl, index) => {
            applyCardTemplateStyles(cards[index], cardEl);
            attachCardEventHandlers(cardEl, cards[index]);
        });
    }, 50);
}

function attachCardEventHandlers(cardEl: Element, card: Card): void {
    if ((cardEl as any).__eventHandlersAttached) {
        return;
    }

    const campaignAttributes = getCampaignAttributes(card);
    const cardClickHandler = (event: Event) => {
        const target = event.target as Element;
        if (!target.closest('.ab-close-button')) {
            track({
                eventName: 'NotificationCardClicked',
                eventData: campaignAttributes,
            });
        }
    };

    cardEl.addEventListener('click', cardClickHandler);

    const closeButton = cardEl.querySelector('.ab-close-button');
    if (closeButton) {
        const closeClickHandler = (event: Event) => {
            event.stopPropagation();
            track({
                eventName: 'NotificationCloseButtonClicked',
                eventData: {},
            });
        };

        closeButton.addEventListener('click', closeClickHandler);
    }

    (cardEl as any).__eventHandlersAttached = true;
}

function applyCardTemplateStyles(card: Card, cardEl: Element): void {
    const templateValue = Number(card?.extras?.template);
    const templateIndex =
        !templateValue || ![1, 2, 3].includes(templateValue)
            ? DEFAULT_NOTIFICATION_TEMPLATE_ID
            : templateValue;

    const { color, icon: Icon } = NOTIFICATION_TEMPLATES[templateIndex];

    (cardEl as HTMLElement).style.setProperty('--card-accent-color', color);

    if (Icon && !cardEl.querySelector('.card-icon')) {
        const iconContainer = document.createElement('div');
        iconContainer.className = 'card-icon';

        ReactDOM.render(<Icon />, iconContainer);
        cardEl.prepend(iconContainer);
    }
}

function attachDrawerScrollListener(): void {
    const feed = document.querySelector('.ab-feed') as HTMLElement;
    if (!feed || feed.classList.contains('listener-attached')) return;

    const handleScroll = () => {
        const isScrolled =
            (document.body.scrollTop || document.documentElement.scrollTop) >
            10;
        feed.classList.toggle('scrolled', isScrolled);
    };

    const scrollContainer = document.querySelector('body');
    scrollContainer?.addEventListener('scroll', handleScroll);
    window.addEventListener('scroll', handleScroll); //for events option from side panel
    feed.classList.add('listener-attached');
    handleScroll();
}

function attachDrawerDismissHandler(): void {
    const drawer = document.querySelector('.ab-feed');
    if (!drawer) return;

    if ((drawer as any).__dismissHandlerAttached) {
        return;
    }

    const handleClickOutside = async (event: MouseEvent) => {
        if (!drawer.contains(event.target as Node)) {
            track({
                eventName: 'NotificationDrawerCloseClicked',
                eventData: {},
            });
            const braze = await getBrazeInstance();
            braze.hideContentCards();
            document.removeEventListener('mousedown', handleClickOutside);

            (drawer as any).__dismissHandlerAttached = false;
        }
    };

    document.addEventListener('mousedown', handleClickOutside);
    (drawer as any).__dismissHandlerAttached = true;
}

function getCampaignAttributes(card: Card): Record<string, string> {
    const { campaign_name, campaign_id, variant_name, step_name } =
        card?.extras || {};

    return {
        campaign_name: campaign_name || 'NA',
        campaign_id: campaign_id || 'NA',
        variant_name: variant_name || 'NA',
        step_name: step_name || 'NA',
    };
}
